import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { createFileRoute } from "@tanstack/react-router";
import { AnimatePresence, m } from "framer-motion";
import { CalendarCheck, Check, Footprints } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { setLocale, type SupportedLocale } from "@/i18n/i18n";
import { useForcedTheme } from "@/lib/hooks/use-forced-theme";
import { useNoIndexMeta } from "@/lib/hooks/use-noindex-meta";
import { tLocalized, type LocalizedString } from "@/types/localized-string";

/**
 * Public kiosk check-in — designed for tablets at clinic entrance.
 * No auth required. Ported 1:1 from legacy
 * `src/app/display/[facilityId]/checkin/page.tsx` + `checkin-kiosk.tsx`.
 *
 * Query params:
 *   lang — locale override (default: "ro" — kiosks are clinic hardware,
 *          Romanian-first like the legacy next-intl default)
 *
 * Example: /display/abc-123/checkin?lang=en
 */

const KIOSK_LANGS: SupportedLocale[] = ["ro", "en", "bg", "de", "fr", "hu", "it", "el"];

interface CheckinSearchParams {
  lang?: SupportedLocale;
}

type KioskStep = "choose" | "appointment" | "walkin_name" | "confirmation";

interface CheckinResult {
  queueNumber: number;
  patientName: string;
  type: string;
  estimatedWaitMinutes: number;
  waitingAhead: number;
}

export const Route = createFileRoute("/display/$facilityId/checkin")({
  validateSearch: (search: Record<string, unknown>): CheckinSearchParams => {
    const langRaw = typeof search.lang === "string" ? search.lang : undefined;
    return {
      lang:
        langRaw && KIOSK_LANGS.includes(langRaw as SupportedLocale)
          ? (langRaw as SupportedLocale)
          : undefined,
    };
  },
  component: CheckinKioskPage,
});

function CheckinKioskPage() {
  const { facilityId } = Route.useParams();
  const { lang } = Route.useSearch();
  const { t, i18n } = useLingui();

  useNoIndexMeta();
  // Legacy kiosk is always dark (fixed teal→gray→indigo gradient).
  useForcedTheme("dark");

  // Locale from the lang param; legacy defaulted to "ro" when missing/invalid.
  useEffect(() => {
    const target = lang ?? "ro";
    if (target !== i18n.locale) {
      void setLocale(target);
    }
  }, [lang, i18n.locale]);

  const [step, setStep] = useState<KioskStep>("choose");
  const [appointmentCode, setAppointmentCode] = useState("");
  const [walkinName, setWalkinName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CheckinResult | null>(null);
  const [facilityName, setFacilityName] = useState<LocalizedString | null>(null);

  // The kiosk greets with the clinic's own name. The public queue endpoint is
  // the one facility read this page already relies on (masked, no ids); on a
  // failure the header falls back to the PontiMed wordmark.
  useEffect(() => {
    let active = true;
    apiFetch
      .get<{ facilityName: LocalizedString | null }>(`/api/facilities/${facilityId}/queue`, {
        skipAuthRefresh: true,
      })
      .then((data) => {
        if (active) setFacilityName(data.facilityName ?? null);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [facilityId]);
  const clinicName = tLocalized(facilityName, i18n.locale);

  const resetToStart = useCallback(() => {
    setStep("choose");
    setAppointmentCode("");
    setWalkinName("");
    setError(null);
    setResult(null);
    setLoading(false);
  }, []);

  // Auto-reset after confirmation
  const showConfirmation = useCallback(
    (data: CheckinResult) => {
      setResult(data);
      setStep("confirmation");
      setTimeout(resetToStart, 15_000); // auto-reset after 15s
    },
    [resetToStart],
  );

  const handleCheckinError = useCallback(
    (err: unknown) => {
      if (err instanceof ApiError) {
        // The API returns plain-English `error` strings (incl. the 409
        // "Already checked in" case) — surfaced verbatim like legacy.
        const body = err.body as { error?: string } | null;
        setError(body?.error || t`Something went wrong. Please try again.`);
      } else {
        setError(t`Connection error. Please try again.`);
      }
    },
    [t],
  );

  const handleAppointmentCheckin = async () => {
    if (!appointmentCode.trim()) return;
    setLoading(true);
    setError(null);

    try {
      const data = await apiFetch.post<CheckinResult>(
        `/api/facilities/${facilityId}/checkin`,
        { appointmentId: appointmentCode.trim(), type: "appointment" },
        { skipAuthRefresh: true },
      );
      showConfirmation(data);
    } catch (err) {
      handleCheckinError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleWalkinCheckin = async () => {
    if (!walkinName.trim()) return;
    setLoading(true);
    setError(null);

    try {
      const data = await apiFetch.post<CheckinResult>(
        `/api/facilities/${facilityId}/checkin`,
        { patientName: walkinName.trim(), type: "walk_in" },
        { skipAuthRefresh: true },
      );
      showConfirmation(data);
    } catch (err) {
      handleCheckinError(err);
    } finally {
      setLoading(false);
    }
  };

  const pageTransition = {
    initial: { opacity: 0, x: 40 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -40 },
    transition: { duration: 0.25 },
  };

  return (
    <div className="from-primary-soft via-background to-accent-soft text-foreground flex min-h-dvh flex-col items-center justify-center bg-gradient-to-br p-8">
      <AnimatePresence mode="wait">
        {/* ===== STEP 1: Choose type ===== */}
        {step === "choose" && (
          <m.div key="choose" {...pageTransition} className="w-full max-w-2xl text-center">
            <div className="mb-12">
              <h1 className="mb-3 text-5xl font-bold tracking-tight">{clinicName || "PontiMed"}</h1>
              <p className="text-muted-foreground text-xl">
                <Trans>Welcome! How would you like to check in?</Trans>
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6">
              <m.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setStep("appointment")}
                className="bg-primary text-primary-foreground hover:bg-primary-hover shadow-primary/25 min-h-[100px] w-full rounded-lg px-6 py-8 text-2xl font-semibold shadow-lg transition-colors"
              >
                <CalendarCheck className="mx-auto mb-2 size-9" aria-hidden />
                <Trans>I have an appointment</Trans>
              </m.button>

              <m.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setStep("walkin_name")}
                className="bg-accent text-accent-foreground shadow-accent/25 hover:bg-accent/90 min-h-[100px] w-full rounded-lg px-6 py-8 text-2xl font-semibold shadow-lg transition-colors"
              >
                <Footprints className="mx-auto mb-2 size-9" aria-hidden />
                <Trans>No appointment (walk-in)</Trans>
              </m.button>
            </div>
          </m.div>
        )}

        {/* ===== STEP 2A: Appointment code ===== */}
        {step === "appointment" && (
          <m.div key="appointment" {...pageTransition} className="w-full max-w-xl text-center">
            <h2 className="mb-2 text-3xl font-bold">
              <Trans>Enter appointment code</Trans>
            </h2>
            <p className="text-muted-foreground mb-8 text-lg">
              <Trans>Enter the code from your appointment confirmation</Trans>
            </p>

            <input
              type="text"
              value={appointmentCode}
              onChange={(e) => setAppointmentCode(e.target.value)}
              placeholder={t`Appointment code`}
              autoFocus
              className="border-input bg-card text-foreground placeholder:text-muted-foreground focus:border-primary w-full rounded-xl border-2 px-6 py-5 text-center font-mono text-2xl tracking-widest focus:outline-none"
              onKeyDown={(e) => e.key === "Enter" && handleAppointmentCheckin()}
            />

            {error && (
              <m.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-destructive mt-4 text-lg"
              >
                {error}
              </m.p>
            )}

            <div className="mt-8 flex gap-4">
              <m.button
                whileTap={{ scale: 0.97 }}
                onClick={resetToStart}
                className="bg-muted text-foreground hover:bg-muted/80 min-h-[60px] flex-1 rounded-lg py-5 text-xl font-medium transition-colors"
              >
                <Trans>Back</Trans>
              </m.button>
              <m.button
                whileTap={{ scale: 0.97 }}
                onClick={handleAppointmentCheckin}
                disabled={loading || !appointmentCode.trim()}
                className="bg-primary text-primary-foreground hover:bg-primary-hover min-h-[60px] flex-1 rounded-lg py-5 text-xl font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? <Trans>Checking in...</Trans> : <Trans>Check In</Trans>}
              </m.button>
            </div>
          </m.div>
        )}

        {/* ===== STEP 2B: Walk-in name ===== */}
        {step === "walkin_name" && (
          <m.div key="walkin" {...pageTransition} className="w-full max-w-xl text-center">
            <h2 className="mb-2 text-3xl font-bold">
              <Trans>Enter your name</Trans>
            </h2>
            <p className="text-muted-foreground mb-8 text-lg">
              <Trans>Your name will appear on the waiting screen</Trans>
            </p>

            <input
              type="text"
              value={walkinName}
              onChange={(e) => setWalkinName(e.target.value)}
              placeholder={t`First and last name`}
              autoFocus
              className="border-input bg-card text-foreground placeholder:text-muted-foreground focus:border-accent w-full rounded-xl border-2 px-6 py-5 text-center text-2xl focus:outline-none"
              onKeyDown={(e) => e.key === "Enter" && handleWalkinCheckin()}
            />

            {error && (
              <m.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-destructive mt-4 text-lg"
              >
                {error}
              </m.p>
            )}

            <div className="mt-8 flex gap-4">
              <m.button
                whileTap={{ scale: 0.97 }}
                onClick={resetToStart}
                className="bg-muted text-foreground hover:bg-muted/80 min-h-[60px] flex-1 rounded-lg py-5 text-xl font-medium transition-colors"
              >
                <Trans>Back</Trans>
              </m.button>
              <m.button
                whileTap={{ scale: 0.97 }}
                onClick={handleWalkinCheckin}
                disabled={loading || !walkinName.trim()}
                className="bg-accent text-accent-foreground hover:bg-accent/90 min-h-[60px] flex-1 rounded-lg py-5 text-xl font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? <Trans>Checking in...</Trans> : <Trans>Check In</Trans>}
              </m.button>
            </div>
          </m.div>
        )}

        {/* ===== STEP 3: Confirmation ===== */}
        {step === "confirmation" && result && (
          <m.div key="confirmation" {...pageTransition} className="w-full max-w-xl text-center">
            <m.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", damping: 15, stiffness: 200 }}
              className="mb-8"
            >
              <div className="border-primary bg-primary-soft mx-auto mb-6 flex h-32 w-32 items-center justify-center rounded-full border-4">
                <Check className="text-primary size-16" strokeWidth={3} aria-hidden />
              </div>
              <h2 className="mb-2 text-3xl font-bold">
                <Trans>You're checked in!</Trans>
              </h2>
              <p className="text-muted-foreground text-lg">{result.patientName}</p>
            </m.div>

            <m.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="border-primary bg-card mb-8 rounded-2xl border-2 p-8"
            >
              <p className="text-muted-foreground mb-2 text-xl tracking-widest uppercase">
                <Trans>Your number</Trans>
              </p>
              <p className="text-primary text-9xl font-black tabular-nums">#{result.queueNumber}</p>
            </m.div>

            <ConfirmationEstimates result={result} />

            <m.button
              whileTap={{ scale: 0.97 }}
              onClick={resetToStart}
              className="bg-muted text-foreground hover:bg-muted/80 mt-8 min-h-[60px] w-full rounded-lg py-5 text-xl font-medium transition-colors"
            >
              <Trans>Done</Trans>
            </m.button>
          </m.div>
        )}
      </AnimatePresence>
      {clinicName ? (
        <p className="text-muted-foreground mt-10 text-sm tracking-wide">PontiMed</p>
      ) : null}
    </div>
  );
}

function ConfirmationEstimates({ result }: { result: CheckinResult }) {
  const { estimatedWaitMinutes, waitingAhead } = result;
  return (
    <div className="bg-card/50 rounded-xl p-4">
      <p className="text-foreground/80 text-xl">
        <Trans>Estimated wait: ~{estimatedWaitMinutes} minutes</Trans>
      </p>
      <p className="text-muted-foreground mt-1">
        <Trans>{waitingAhead} patients ahead of you</Trans>
      </p>
    </div>
  );
}
